import type { AppInitData, DealerResponse, WebsiteResponse } from '@/types/core';
import { ValidatedConfig } from '@/entities/config';

export interface Settings {
	activeDutyServiceMember: boolean;
	recentGraduate: boolean;
	ownedMakes: string[];
}

export enum Sources {
	App = 'app',
	Email = 'email',
}

export interface WebsiteSettings {
	dealerName: string;
	precisePriceFunction: boolean;
	leaseEnabled: boolean;
	leaseMSTEnabled: boolean;
	priceGating: number;
	leadGatingType: number;
	creditAppSource: number;
	paymentTerms: {
		finance: {
			financeMinimumDownPayment: number;
			financeMinimumDownPaymentType: number;
			defaultDownPayment: number;
			defaultDownPaymentType: number;
			defaultTermLength: number;
			defaultInterestRate: number;
			defaultCreditScore: number;
			minimumFinanceCredit: number;
		};
		lease: {
			leaseMinimumDownPayment: number;
			leaseMinimumDownPaymentType: number;
			leaseDownPayment: number;
			leaseDownPaymentType: number;
			leaseLength: number;
			milesPerYear: number;
			leaseCreditScore: number;
			minimumLeaseCredit: number;
		};
	};
	incentives: {
		advertisedOffersOptions: number;
	};
	lenderSocketEnabled: boolean;
	rebates: {
		discount: {
			discountEnabled: boolean;
			priceRuleDiscountEnabled: boolean;
			displayMarkups: boolean;
		};
		privateDiscount: {
			privateDiscountEnabled: boolean;
		};
		manufacturerRebates: {
			manufacturerRebatesEnabled: boolean;
			eligibleIncentivesEnabled: boolean;
		};
	};
	testDrive: {
		testDriveEnabled: boolean;
		homeTestDriveEnabled: boolean;
		homeTestDriveDistance: number;
	};
	tradeIn: {
		tradeInSource: number;
		tradeInOperation: string;
		tradeInValue: number;
		tradeInPercent: number;
	};
	protection: {
		protectionPriceType: number;
		isShowEmptyPriceProtection: number;
		isShowEmptyPriceAftermarket: number;
		fiService: number;
	};
	vehiclePrice: {
		new: {
			originalPrice: number;
			reducedPrice: number;
			cashReducedPrice: number;
			leaseReducedPrice: number;
			specialPrice: boolean;
		};
		used: {
			originalPrice: number;
			reducedPrice: number;
			cashReducedPrice: number;
			leaseReducedPrice: number;
			specialPrice: boolean;
		};
	};
	account: {
		inStoreTool: number;
		accountTypeWebsiteEnabled: boolean;
		accountType: number;
		salesEventSource: number;
		tier: number;
	};
	displayOptions: {
		dealTypeShowFirst: number;
		showAdvertisedOfferFirst: boolean;
		showTimeSavingText: number;
	};
	widgetDisplay: {
		vdpFinanceWidgetEnabled: boolean;
		vdpLeaseWidgetEnabled: boolean;
		vdpWidgetOrder: number;
	};
	creditAppSourceLenderSocket: string;
}

export class CoreState {
	isModalOpen = false

	initData: AppInitData | null = null

	styleVariables = {}

	config: ValidatedConfig = {
		websiteId: '',
		apiKey: '',
		buttonText: '',
	}

	settingsLoaded = false

	settings: Settings = {
		activeDutyServiceMember: false,
		recentGraduate: false,
		ownedMakes: [],
	}

	clientId: null | string = null

	website: WebsiteResponse | null = null

	dealer: DealerResponse | null = null

	isError = false

	originSource: Sources = Sources.App

	websiteSettings: WebsiteSettings = {
		dealerName: '',
		precisePriceFunction: true,
		leaseEnabled: true,
		leaseMSTEnabled: false,
		priceGating: 1,
		leadGatingType: 0,
		creditAppSource: 0,
		paymentTerms: {
			finance: {
				financeMinimumDownPayment: 0,
				financeMinimumDownPaymentType: 1,
				defaultDownPayment: 3000,
				defaultDownPaymentType: 1,
				defaultTermLength: 60,
				defaultInterestRate: 5.9,
				defaultCreditScore: 680,
				minimumFinanceCredit: 580,
			},
			lease: {
				leaseMinimumDownPayment: 1000,
				leaseMinimumDownPaymentType: 1,
				leaseDownPayment: 2000,
				leaseDownPaymentType: 1,
				leaseLength: 39,
				milesPerYear: 15000,
				leaseCreditScore: 700,
				minimumLeaseCredit: 580,
			},
		},
		incentives: {
			advertisedOffersOptions: 1,
		},
		lenderSocketEnabled: false,
		rebates: {
			discount: {
				discountEnabled: true,
				priceRuleDiscountEnabled: false,
				displayMarkups: false,
			},
			privateDiscount: {
				privateDiscountEnabled: false,
			},
			manufacturerRebates: {
				manufacturerRebatesEnabled: true,
				eligibleIncentivesEnabled: true,
			},
		},
		testDrive: {
			testDriveEnabled: true,
			homeTestDriveEnabled: false,
			homeTestDriveDistance: 0,
		},
		tradeIn: {
			tradeInSource: 2,
			tradeInOperation: '-',
			tradeInValue: 0,
			tradeInPercent: 0,
		},
		protection: {
			protectionPriceType: 1,
			isShowEmptyPriceProtection: 0,
			isShowEmptyPriceAftermarket: 0,
			fiService: 0,
		},
		vehiclePrice: {
			new: {
				originalPrice: 2,
				reducedPrice: 1,
				cashReducedPrice: 1,
				leaseReducedPrice: 1,
				specialPrice: true,
			},
			used: {
				originalPrice: 1,
				reducedPrice: 1,
				cashReducedPrice: 1,
				leaseReducedPrice: 1,
				specialPrice: true,
			},
		},
		account: {
			inStoreTool: 0,
			accountTypeWebsiteEnabled: true,
			accountType: 1,
			salesEventSource: 1,
			tier: 1,
		},
		displayOptions: {
			dealTypeShowFirst: 1,
			showAdvertisedOfferFirst: false,
			showTimeSavingText: 1,
		},
		widgetDisplay: {
			vdpFinanceWidgetEnabled: true,
			vdpLeaseWidgetEnabled: false,
			vdpWidgetOrder: 0,
		},
		creditAppSourceLenderSocket: 'NA',
	}
}
